import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo, useState } from 'react';
import { validateFeatureFlagName } from '../constants';
import { FlagState } from '../types/FlagState';
import { useFeatureFlagsReady } from './useFeatureFlagsReady';

export function useFeatureFlag<TValue = unknown>(featureFlag: string, defaultValue?: TValue): FlagState<TValue> {
  const client = useLDClient();
  const ready = useFeatureFlagsReady();

  validateFeatureFlagName(featureFlag);

  const [state, setState] = useState(defaultValue ?? false);

  useEffect(() => {
    // waiting for client to get ready
    if (!client || !ready) {
      return;
    }

    const currentValue = client.variation(featureFlag, defaultValue ?? false);
    setState(currentValue);

    const event = `change:${featureFlag}`;
    const handler = (value: TValue) => {
      setState(value ?? false);
    };

    client.on(event, handler);
    return () => client.off(event, handler);
  }, [client, ready, featureFlag]);

  return useMemo(
    () => ({
      enabled: !!state,
      value: state as TValue,
      ready,
    }),
    [state, ready],
  );
}
